import { TrackReference } from '@livekit/components-core';
import {
  CarouselLayout,
  FocusLayout,
  ParticipantTile,
  useTrackMutedIndicator,
  VideoTrack,
} from '@livekit/components-react';
import { Track } from 'livekit-client';
import { useEffect, useRef, useState } from 'react';
import { LayoutProps } from './common';

const FRAME_DECODE_POLL_INTERVAL_MS = 100;

export const hasInboundDecodedFrames = (stats: RTCStatsReport | undefined): boolean => {
  if (!stats) {
    return false;
  }
  return Array.from(stats).some((item) => {
    const report = item[1];
    return (
      report.type === 'inbound-rtp' && ((report as RTCInboundRtpStreamStats).framesDecoded ?? 0) > 0
    );
  });
};

const useScreenFeedHasDecodedFrames = (
  screenShareTrack: TrackReference,
  feedOtherwiseAvailable: boolean,
): boolean => {
  const [hasDecodedFrames, setHasDecodedFrames] = useState(false);
  const trackSid = screenShareTrack.publication?.trackSid;
  // useTracks rebuilds TrackReference wrappers on routine room events; keep the
  // latest wrapper in a ref and key the effect on trackSid so identity churn
  // does not reset hasDecodedFrames (placeholder flash).
  const screenShareTrackRef = useRef(screenShareTrack);
  screenShareTrackRef.current = screenShareTrack;

  useEffect(() => {
    setHasDecodedFrames(false);

    if (!feedOtherwiseAvailable) {
      return;
    }

    let cancelled = false;
    let interval: ReturnType<typeof setInterval>;

    const pollDecodedFrames = async () => {
      const activeVideoTrack = screenShareTrackRef.current.publication?.videoTrack;
      if (!activeVideoTrack) {
        return;
      }

      try {
        const stats = await activeVideoTrack.getRTCStatsReport();
        if (cancelled) {
          return;
        }
        if (hasInboundDecodedFrames(stats)) {
          setHasDecodedFrames(true);
          clearInterval(interval);
        }
      } catch {
        // Keep polling until the first decoded frame or unmount.
      }
    };

    void pollDecodedFrames();

    interval = setInterval(() => {
      if (cancelled) {
        return;
      }
      void pollDecodedFrames();
    }, FRAME_DECODE_POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [feedOtherwiseAvailable, trackSid]);

  return hasDecodedFrames;
};

const ScreenFeedPlaceholder = () => (
  <div className="screen-placeholder">Screen feed unavailable</div>
);

const ScreenShareRegionContent = ({ screenShareTrack }: { screenShareTrack: TrackReference }) => {
  const { isMuted } = useTrackMutedIndicator(screenShareTrack);
  const mediaStreamTrack = screenShareTrack.publication?.track?.mediaStreamTrack;
  const isFeedUnavailable =
    isMuted || (mediaStreamTrack != null && mediaStreamTrack.readyState !== 'live');
  const hasDecodedFrames = useScreenFeedHasDecodedFrames(screenShareTrack, !isFeedUnavailable);

  if (isFeedUnavailable || !hasDecodedFrames) {
    return <ScreenFeedPlaceholder />;
  }

  return <FocusLayout trackRef={screenShareTrack} />;
};

const ScreenShareRegion = ({ screenShareTrack }: { screenShareTrack?: TrackReference }) => {
  if (!screenShareTrack) {
    return <ScreenFeedPlaceholder />;
  }

  return <ScreenShareRegionContent screenShareTrack={screenShareTrack} />;
};

const WebcamBottomLayout = ({ tracks: references, keepGrid = true }: LayoutProps) => {
  const screenShareTrack = references.find(
    (track) =>
      track.source === Track.Source.ScreenShare || track.source === Track.Source.ScreenShareAudio,
  );
  const webcamTrack = references.find((track) => track.source === Track.Source.Camera);

  if (!screenShareTrack && !webcamTrack) {
    return <></>;
  }

  if (!keepGrid) {
    if (screenShareTrack && !webcamTrack) {
      return <VideoTrack trackRef={screenShareTrack as TrackReference} />;
    }
    if (!screenShareTrack && webcamTrack) {
      return <VideoTrack trackRef={webcamTrack as TrackReference} />;
    }
  }

  return (
    <div className="lk-webcam-bottom-layout">
      <div className="webcam-container">
        {webcamTrack && (
          <CarouselLayout tracks={[webcamTrack]}>
            <ParticipantTile />
          </CarouselLayout>
        )}
      </div>
      <div className="main-content">
        <ScreenShareRegion screenShareTrack={screenShareTrack as TrackReference | undefined} />
      </div>
    </div>
  );
};

export default WebcamBottomLayout;
